import type { QueryArgFrom } from '../rtk-query-types';
import type { AsyncQueryFlowPrefetchOptions, QueryFlowFactory } from './buildEndpointQueryFlow';
import type { AnyQueryDefinition } from './types';

// Набор дополнительных методов эндпоинта, которые добавляются в RTK Query.
export type EndpointPrefetch<Definition extends AnyQueryDefinition> = {
    /**
     * Предзагружает данные запроса заранее (например, при переходе между страницами).
     *
     * В отличие от `query()`, удерживает предзагруженные данные в кеше через подписку на поток,
     * пока на них не появится настоящая подписка (монтирование компонента-потребителя) либо
     * не истечет `keepPrefetchedDataFor`. Это гарантирует, что данные не будут удалены из кеша
     * между предзагрузкой и первым рендером потребителя — что особенно важно для эндпоинтов
     * с `keepUnusedDataFor: 0`, у которых иначе данные удаляются сразу после завершения запроса.
     *
     * Возвращаемый промис никогда не реджектится.
     */
    prefetch: (queryArgs: QueryArgFrom<Definition>, options?: AsyncQueryFlowPrefetchOptions) => Promise<void>;
};

// Параметры для `buildEndpointPrefetch()`.
type BuildPrefetchOptions = {
    queryFlow: QueryFlowFactory<AnyQueryDefinition>;
};

// Создает метод предзагрузки данных эндпоинта — тонкую обертку над queryFlow.prefetch().
export function buildEndpointPrefetch({
    queryFlow,
}: BuildPrefetchOptions): EndpointPrefetch<AnyQueryDefinition>['prefetch'] {
    return function prefetch(queryArgs, options) {
        return queryFlow(queryArgs, { prefetch: false }).prefetch(options);
    };
}
